import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  MoreVertical, 
  Pencil, 
  Trash2, 
  ChevronDown, 
  ChevronUp, 
  FolderPlus, 
  FileText,
  ChevronRight, 
  RefreshCw,
  Search,
  Check,
   X,
   Share2,  
  Crown,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSession } from '../../context/SessionContext';
import { useModel } from '../../context/ModelContext';
import { isVoiceModel } from '../../utils/NovaVoiceManager';
import { useHat } from '../../context/HatContext';
import { useKnowledgeBase } from '../../context/KnowledgeBaseContext';
import { useAuth } from '../../context/AuthContext';
import { useUser } from '../../context/UserContext';
import ShareProjectDialog from './ShareProjectDialog';
import DeleteProjectDialog from './DeleteProjectDialog';

// Import API service functions
import { 
  createProject as apiCreateProject, 
  getProjects as apiGetProjects,
  updateProject as apiUpdateProject, 
  deleteProject as apiDeleteProject,
  restoreDeletedProject as apiRestoreDeletedProject,
  addSessionToProject,
  removeSessionFromProject,
  getProjectById as apiGetProjectById,
  getProjectSessionsWithOwners,
} from '../../service/apiService';
import { formatWindowTime } from '../../utils/deletionWindow';

export const ProjectsService = {
  refreshCallback: null as (() => void) | null,
  
  setRefreshCallback(callback: () => void) {
    this.refreshCallback = callback;
  },
  
  refresh() {
    if (this.refreshCallback) {
      this.refreshCallback();
    }
  }
};

/**
 * Projects the user has already opened, with the project's timestamp at the
 * moment they opened it. A project whose timestamp has moved on since then --
 * a conversation was added to it, or it changed some other way -- is marked in
 * the list until it is clicked. Stored per user in localStorage so the mark
 * survives a refresh, which is the whole point of it.
 */
const SEEN_PROJECTS_KEY = "kyva.projectsSeen";

const projectStamp = (project: any): string =>
  project?.updated_at || project?.created_at || "";

const readSeenProjects = (userId: string): Record<string, string> | null => {
  try {
    const raw = localStorage.getItem(`${SEEN_PROJECTS_KEY}.${userId}`);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // Private windows and blocked site data: fall back to "nothing seen".
    return null;
  }
};

const writeSeenProjects = (userId: string, seen: Record<string, string>) => {
  try {
    localStorage.setItem(`${SEEN_PROJECTS_KEY}.${userId}`, JSON.stringify(seen));
  } catch {
    // Not being able to remember is not worth an error the user can act on.
  }
};

interface ShareDialogProject {
  id: string;
  name: string;
  isOwner: boolean;
}

const Projects: React.FC = () => {
const { sessions, setSessionId, loadSessionConversation } = useSession();
const { models, setSelectedModel } = useModel();
const { restoreSessionHat } = useHat();
const { restoreSessionKnowledgeBase } = useKnowledgeBase();

  const [projects, setProjects] = useState<any[]>([]);
  const [showProjects, setShowProjects] = useState<boolean>(true);
  const [menuOpenId, setMenuOpenId] = useState<string | null>(null);
  const [renamingProjectId, setRenamingProjectId] = useState<string | null>(null);
  const [newProjectName, setNewProjectName] = useState<string>("");
  const [isCreatingProject, setIsCreatingProject] = useState<boolean>(false);
  const [newProject, setNewProject] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [deletingProjectId, setDeletingProjectId] = useState<string | null>(null);
  
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(null);
  const [loadingSessions, setLoadingSessions] = useState<string | null>(null);
  const [projectSessions, setProjectSessions] = useState<Record<string, any[]>>({}); 

const [shareDialogOpen, setShareDialogOpen] = useState<boolean>(false);
const [shareDialogProject, setShareDialogProject] = useState<ShareDialogProject | null>(null);
  // Super admin delete confirmation (owners delete without a prompt, as before)
  const [deleteDialogProject, setDeleteDialogProject] = useState<any | null>(null);
  
  const [searchTerm, setSearchTerm] = useState("");
  
  const [seenProjects, setSeenProjects] = useState<Record<string, string>>({});

  const [hoveredProject, setHoveredProject] = useState<string | null>(null);
  const [hoveredSession, setHoveredSession] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const { userDetails } = useUser();
  // Elevated access from SUPER_ADMIN_EMAILS on the api service: share, rename
  // and delete any project shared with them, not only their own.
  const isSuperAdmin = userDetails?.is_super_admin === true;
  const canManageProject = (project: any) => project.is_owner !== false || isSuperAdmin;
  const currentUserId = sessionStorage.getItem("userEmail") || sessionStorage.getItem("userId") || ""; 
  const menuRef = React.useRef<HTMLDivElement>(null);
  
  const filteredProjects = searchTerm.trim() 
    ? projects.filter(project => 
        project.project_name.toLowerCase().includes(searchTerm.toLowerCase()))
    : projects;
  
  // Function to fetch projects
  const getAllUserProjects = async () => {
  setIsLoading(true);
  try {
    const projectsData = await apiGetProjects();
    const list = Array.isArray(projectsData) ? projectsData : [];
    setProjects(list);

    const stored = readSeenProjects(currentUserId);
    if (stored) {
      setSeenProjects(stored);
    } else {
      // First run on this device: take the current state as the baseline so
      // the whole list does not light up at once.
      const baseline: Record<string, string> = {};
      list.forEach((project: any) => {
        baseline[project.id] = projectStamp(project);
      });
      writeSeenProjects(currentUserId, baseline);
      setSeenProjects(baseline);
    }
  } catch (error) {
    console.error('Error fetching projects:', error);
    toast.error("Failed to load projects");
  } finally {
    setIsLoading(false);
  }
};
  
  // Register the fetchProjects function with the service
  useEffect(() => {
    ProjectsService.setRefreshCallback(getAllUserProjects);
    
    return () => {
      // Cleanup when component unmounts
      if (ProjectsService.refreshCallback === getAllUserProjects) {
        ProjectsService.refreshCallback = null;
      }
    };
  }, []);
  
  // Load initial projects
useEffect(() => {
  // Wait for auth to complete, then fetch projects
  if (!authLoading && isAuthenticated) {
    getAllUserProjects();
  }
}, [authLoading, isAuthenticated]);
  
  // Handle outside clicks for menu
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuOpenId &&
        menuRef.current &&
        !menuRef.current.contains(event.target as Node)
      ) {
        setMenuOpenId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [menuOpenId]);
  
  const handleProjectMouseEnter = (event: React.MouseEvent, projectId: string, projectName: string) => {
    setHoveredProject(projectId);
    updateTooltipPosition(event);
  };

  const handleSessionMouseEnter = (event: React.MouseEvent, sessionId: string, sessionTitle: string) => {
    setHoveredSession(sessionId);
    updateTooltipPosition(event);
  };

  const updateTooltipPosition = (event: React.MouseEvent) => {
    setTooltipPosition({
      top: event.clientY + 20, 
      left: event.clientX 
    });
  };

  const handleProjectMouseLeave = () => {
    setHoveredProject(null);
  };

  const handleSessionMouseLeave = () => {
    setHoveredSession(null);
  };

  const handleProjectMouseMove = (event: React.MouseEvent) => {
    if (hoveredProject) {
      updateTooltipPosition(event);
    }
  };

  const handleSessionMouseMove = (event: React.MouseEvent) => {
    if (hoveredSession) {
      updateTooltipPosition(event);
    }
  };
  
  // Toggle project list visibility
  const toggleProjects = () => {
    setShowProjects(!showProjects);
  };
  
  const toggleMenu = (projectId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setMenuOpenId((prevId) => (prevId === projectId ? null : projectId));
  };
  
  // Start project creation process
  const startProjectCreation = () => {
    setIsCreatingProject(true);
    setNewProject("");
  };
  
  // Cancel project creation
  const cancelProjectCreation = () => {
    setIsCreatingProject(false);
    setNewProject("");
  };
  
  // Create a new project
  const handleCreateProject = async () => {
    if (!newProject.trim()) {
      toast.error("Project name cannot be empty");
      return;
    }
    
    setIsLoading(true);
    try {
      const response = await apiCreateProject(newProject.trim());
      
      if (response && response.project_id) {
        toast.success("Project created successfully");
        
        const updatedProjects = await apiGetProjects();
        setProjects(Array.isArray(updatedProjects) ? updatedProjects : []);
      } else {
        toast.error("Failed to create project");
      }
      
      setIsCreatingProject(false);
      setNewProject("");
    } catch (error) {
      console.error("Error creating project:", error);
      toast.error("Failed to create project");
    } finally {
      setIsLoading(false);
    }
  };
  
  const startRenaming = (projectId: string, currentName: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setRenamingProjectId(projectId);
    setNewProjectName(currentName);
    setMenuOpenId(null);
  };
  
  const handleRenameCancel = () => {
    setRenamingProjectId(null);
    setNewProjectName("");
  };
  
  const handleRenameSave = async (projectId: string) => {
    if (!newProjectName.trim()) {
      toast.error("Project name cannot be empty");
      return;
    }
    
    setIsLoading(true);
    try {
      await apiUpdateProject(projectId, newProjectName.trim());
      setProjects(projects.map(project => 
        project.id === projectId 
          ? { ...project, project_name: newProjectName.trim() } 
          : project
      ));
      setRenamingProjectId(null);
      setNewProjectName("");
      toast.success("Project updated successfully");
    } catch (error) {
      console.error("Error renaming project:", error);
      toast.error("Failed to rename project");
    } finally {
      setIsLoading(false);
    }
  };
  
  const handleProjectDelete = async (
    projectId: string,
    e: React.MouseEvent | null,
    options: { cascade?: boolean; deleteBrainBanks?: boolean } = {}
  ) => {
    e?.stopPropagation();
    setDeletingProjectId(projectId);
    
    try {
      const result = await apiDeleteProject(projectId, options);
      const deletedProject = projects.find(project => project.id === projectId);
      setProjects(projects.filter(project => project.id !== projectId));
      setMenuOpenId(null);
      setDeleteDialogProject(null);
      if (result?.purge_at) {
        // Super admin cascade: hidden now, purged after the deletion window.
        // Undo restores it; Chat History > Recently deleted does the same later.
        toast.success("Project deleted", {
          description: `${deletedProject?.project_name || "The project"} is hidden for everyone. You can restore it from Recently deleted until ${formatWindowTime(result.purge_at)}.`,
          duration: 10000,
          action: {
            label: "Undo",
            onClick: async () => {
              try {
                await apiRestoreDeletedProject(projectId);
                toast.success("Project restored");
                getAllUserProjects();
              } catch (error) {
                console.error("Error restoring project:", error);
                toast.error("Failed to restore project");
              }
            },
          },
        });
      } else {
        toast.success(options.cascade ? (result?.message || "Project deleted") : "Project deleted successfully");
      }

      if (expandedProjectId === projectId) {
        setExpandedProjectId(null);
      }
    } catch (error) {
      console.error("Error deleting project:", error);
      toast.error("Failed to delete project");
    } finally {
      setDeletingProjectId(null);
    }
  };

  // Super admins confirm first: their delete removes the conversations for
  // everyone and may cascade to BrainBanks. Owners keep the immediate delete.
  const handleDeleteClick = (project: any, e: React.MouseEvent) => {
    e.stopPropagation();
    if (isSuperAdmin) {
      setMenuOpenId(null);
      setDeleteDialogProject(project);
      return;
    }
    handleProjectDelete(project.id, e);
  };

  const openShareDialog = (project: any, e: React.MouseEvent) => {
  e.stopPropagation();
  setShareDialogProject({
    id: project.id,
    name: project.project_name,
    isOwner: canManageProject(project),
  });
  setShareDialogOpen(true);
  setMenuOpenId(null);
};
  
  /** Clears the "new / updated" mark. Clicking the project is the only thing
      that does -- a refresh must not. */
  const markProjectSeen = (projectId: string) => {
    const stamp = projectStamp(projects.find((project) => project.id === projectId));
    setSeenProjects((prev) => {
      if (prev[projectId] === stamp) return prev;
      const next = { ...prev, [projectId]: stamp };
      writeSeenProjects(currentUserId, next);
      return next;
    });
  };

  const isProjectUnseen = (project: any): boolean =>
    seenProjects[project.id] !== projectStamp(project);

  const handleProjectClick = async (projectId: string) => {
  markProjectSeen(projectId);

  if (expandedProjectId === projectId) {
    setExpandedProjectId(null);
    return;
  }
  
  setExpandedProjectId(projectId);

  // ← ADD EVERYTHING BELOW THIS LINE
  if (!projectSessions[projectId]) {
    setLoadingSessions(projectId);
    try {
      const fetchedSessions = await getProjectSessionsWithOwners(projectId);

// ← Normalize session objects to always have id and title
const normalizedSessions = Array.isArray(fetchedSessions)
  ? fetchedSessions.map((session: any) => ({
      ...session,
      // Handle different API response structures
      id: session.id || session.session_id || session.session?.id,
      title: session.title || session.session_title || session.session?.title || "Untitled Conversation",model_id: session.model_id || session.session?.model_id,
      hat_id: session.hat_id || session.session?.hat_id,
    }))
  : [];

console.log("Normalized sessions:", normalizedSessions); // ← Check this in console

setProjectSessions(prev => ({
  ...prev,
  [projectId]: normalizedSessions
}));
    } catch (error) {
      console.error("Error fetching project sessions:", error);
      toast.error("Failed to load project sessions");
    } finally {
      setLoadingSessions(null);
    }
  }
};
  
 const handleSessionClick = (sessionId: string, projectId: string, e: React.MouseEvent) => {
  e.stopPropagation();

  if (!sessionId) {
    console.error("Session ID is undefined or null");
    return;
  }

  const selectedSession =
    sessions.find((session) => session.id === sessionId) ||
    (projectSessions[projectId] || []).find((session) => session.id === sessionId);

  if (selectedSession) {
    if (selectedSession.model_id && models.length > 0) {
      const sessionModel = models.find(
        (model) => model.foundation_model_id === selectedSession.model_id
      );
      // A voice conversation reopens as a typed chat: Nova Sonic only speaks,
      // so the current text model stays selected (and voice mode stays shut).
      if (sessionModel && !isVoiceModel(sessionModel)) {
        setSelectedModel(sessionModel);
      }
    }
    // Show the VA and BrainBank the conversation was started with, not
    // whatever is selected right now. The session records both on its first
    // message; the contexts wait for their lists if those are still loading.
    restoreSessionHat(selectedSession.hat_id);
    restoreSessionKnowledgeBase(sessionId, selectedSession.knowledge_base_id);
  }

  setSessionId(sessionId);
  loadSessionConversation(sessionId);
};
  
  const handleRemoveSession = async (projectId: string, sessionId: string, e: React.MouseEvent) => {
  e.stopPropagation(); 
  
  try {
    await removeSessionFromProject(projectId, sessionId);
    
    setProjectSessions(prev => ({
      ...prev,
      [projectId]: (prev[projectId] || []).filter(
        session => session.id !== sessionId
      )
    }));

    setProjects(projects.map(project => 
      project.id === projectId 
        ? { ...project, session_ids: project.session_ids.filter((id: string) => id !== sessionId) } 
        : project
    ));
    
    toast.success("Conversation removed from project");
  } catch (error) {
    console.error("Error removing conversation from project:", error);
    toast.error("Failed to remove conversation from project");
  }
};
  
  // Function to handle search input changes
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };
  
  return (
    <div className="flex flex-col">
      {/* Header with dropdown */}
      <div
        className="flex items-center justify-between px-3 py-2 rounded-md cursor-pointer bg-surface-overlay shadow-sm hover:bg-surface-2 hover:shadow transition-all text-primary"
        onClick={toggleProjects}
      >
        <div className="flex items-center">
          <FolderPlus size={20} className="text-muted" />
          <span className="ml-3 text-sm font-semibold">Projects</span>
        </div>
        <div className="flex items-center space-x-1">
          <button
            className="p-1 hover:bg-surface-3 rounded-full transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              startProjectCreation();
            }}
          >
            <Plus size={16} className="text-subtle" />
          </button>
          {showProjects ? (
            <ChevronUp size={16} className="text-subtle" />
          ) : (
            <ChevronDown size={16} className="text-subtle" />
          )}
        </div>
      </div>
      
      {/* Projects content area */}
      {showProjects && (
        <div>
          {/* Search input */}
         <div className="mb-2 relative px-2 py-1.5">
           <div className="relative flex items-center">
           <input
           type="text"
           placeholder="Search projects..."
           value={searchTerm}
           onChange={handleSearchChange}
           className="w-full px-3 py-1.5 pr-8 text-xs bg-surface-2 text-primary border border-border-default rounded-md focus:outline-none focus:ring-1 focus:ring-violet-500 focus:border-violet-500 placeholder:text-subtle"
         />
        <Search size={14} className="absolute right-2 text-gray-400" />
          </div>
         </div>  
          {/* Project creation form */}
          {isCreatingProject && (
            <div className="flex items-center px-2 py-1 mx-2 mb-1 rounded-md bg-violet-50">
            <input
              type="text"
               className="flex-grow border p-1 rounded text-xs"
                value={newProject}
                onChange={(e) => setNewProject(e.target.value)}
                placeholder="Project name"
                autoFocus
                onKeyDown={(e) => {
                 if (e.key === 'Enter') handleCreateProject();
                 if (e.key === 'Escape') cancelProjectCreation();
              }}
              />
              <button
                className="ml-2 text-green-600 hover:bg-green-100 rounded-lg p-1"
                onClick={handleCreateProject}
              >
              <Check size={14} />
              </button>
              <button
               className="ml-1 text-red-600 hover:bg-red-100 rounded-lg p-1"
                 onClick={cancelProjectCreation}
              >
                <X size={14} />  {/* ← FIXED: X icon instead of ChevronUp */}
              </button>
            </div>
          )}
          
           {/* Projects list with scrollbar */}
           <div 
           className="projects-scrollbar"
           style={{
           height: "160px",
           overflowY: "scroll",
           borderTop: isLoading ? "none" : "1px solid var(--color-border)",
           borderBottom: isLoading ? "none" : "1px solid var(--color-border)"
         }}
           >
            {isLoading ? (
              <div className="flex justify-center items-center py-2">
                <div className="animate-spin h-4 w-4 border-2 border-violet-700 border-t-transparent rounded-full"></div>
              </div>
            ) : filteredProjects.length > 0 ? (
              <div className="py-1">
                {filteredProjects.map((project) => (
                  <div key={project.id} className="px-2">
                    <div
                      className="group flex items-center justify-between px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 rounded cursor-pointer"
                      onClick={() => handleProjectClick(project.id)}
                    >
                      <div className="flex items-center flex-grow overflow-hidden">
                      {expandedProjectId === project.id ? (
                      <ChevronDown size={14} className="mr-1 text-gray-500 flex-shrink-0" />
                      ) : (
                      <ChevronRight size={14} className="mr-1 text-gray-500 flex-shrink-0" />
                     )}
  
                    {renamingProjectId === project.id ? (
                     <div className="flex items-center w-full" onClick={(e) => e.stopPropagation()}>
                     <input
                     type="text"
                     className="flex-grow bg-white text-gray-900 border p-1 rounded text-xs"
                     value={newProjectName}
                     onChange={(e) => setNewProjectName(e.target.value)}
                     placeholder="Project name"
                     autoFocus
                     onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                    handleRenameSave(project.id);
                   }
                 }}
               />
               <button
               className="ml-1 text-green-600 hover:bg-green-100 rounded-lg p-1"
               onClick={() => handleRenameSave(project.id)}
              >
             <Check size={12} />
             </button>
             <button
             className="ml-1 text-red-600 hover:bg-red-100 rounded-lg p-1"
             onClick={handleRenameCancel}
             >
            <X size={12} />
            </button>
            </div>
         ) : (
        <span 
          className="truncate"
          onMouseEnter={(e) => handleProjectMouseEnter(e, project.id, project.project_name)}
          onMouseLeave={handleProjectMouseLeave}
          onMouseMove={handleProjectMouseMove}
        >
         {project.project_name}
         </span>
         )}
          </div>
             {isProjectUnseen(project) && renamingProjectId !== project.id && (
               <span
                 title="New or updated since you last opened it"
                 className="flex-shrink-0 ml-2 px-1.5 py-0.5 rounded-full bg-violet-100 text-violet-700 text-[10px] font-semibold leading-none"
               >
                 New
               </span>
             )}
             <div className="flex-shrink-0 opacity-0 group-hover:opacity-100">
                     <button 
                          className="p-1 text-gray-500 hover:text-gray-700"
                          onClick={(e) => toggleMenu(project.id, e)}
                        >
                          <MoreVertical size={14} />
                        </button>
                      </div>
                      
                      {/* Menu popup */}
                      {menuOpenId === project.id && (
  <div
    ref={menuRef}
    className="absolute right-4 mt-16 p-1 w-32 rounded-md z-50 bg-surface-overlay text-primary border border-border-default shadow-md ring-1 ring-black/5 dark:ring-gray-700"
  >
    {/* Share button (owner or super admin) */}
    {canManageProject(project) && (
      <button
        className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 text-gray-600 hover:text-gray-700 font-semibold rounded-md flex items-center"
        onClick={(e) => openShareDialog(project, e)}
      >
        <Share2 size={12} className="mr-2" />
        Share
      </button>
    )}

    {/* Rename (owner or super admin) */}
    {canManageProject(project) && (
      <button
        className="w-full text-left px-3 py-1.5 text-xs hover:bg-gray-100 text-gray-600 hover:text-gray-700 font-semibold rounded-md flex items-center"
        onClick={(e) => startRenaming(project.id, project.project_name, e)}
      >
        <Pencil size={12} className="mr-2" />
        Rename
      </button>
    )}

    {/* Delete (owner or super admin) */}
    {canManageProject(project) && (
      <button
        className="w-full text-left px-3 py-1.5 text-xs text-red-600 hover:text-red-500 hover:bg-gray-100 font-semibold rounded-md flex items-center"
        onClick={(e) => handleDeleteClick(project, e)}
      >
        <Trash2 size={12} className="mr-2" />
        Delete
      </button>
    )}

    {/* Shared with you label for non-owners */}
    {project.is_shared === true && project.is_owner === false && (
      <div className="px-3 py-1.5 text-xs text-gray-400 flex items-center">
        <Crown size={12} className="mr-2 text-amber-500" />
        Shared with you
      </div>
    )}
  </div>
)}
                    </div>
                    
                    {/* Project sessions */}
                    {expandedProjectId === project.id && (
                      <div className="pl-7 mt-1 mb-2">
                        {loadingSessions === project.id ? (
                          <div className="flex items-center py-1 text-xs text-gray-500">
                            <div className="animate-spin h-3 w-3 border-2 border-violet-700 border-t-transparent rounded-full mr-2"></div>
                            Loading sessions...
                          </div>
                        ) : (projectSessions[project.id] || []).length > 0 ? (
  <div>
    {(projectSessions[project.id] || []).map(session => (
      <div 
  key={session.id || session.session_id} 
  className="group flex items-center justify-between py-0.5 px-2 text-xs text-gray-700 hover:bg-gray-100 rounded cursor-pointer"
  onClick={(e) => handleSessionClick(session.id || session.session_id, project.id, e)}
>
        <div className="flex items-center overflow-hidden">
          <FileText size={12} className="text-gray-500 mr-1.5 flex-shrink-0" />
          <span 
            className="truncate"
            onMouseEnter={(e) => handleSessionMouseEnter(e, session.id || session.session_id, session.title)}
            onMouseLeave={handleSessionMouseLeave}
            onMouseMove={handleSessionMouseMove}
          >
            {session.title}
          </span>
        </div>
        {canManageProject(project) && (
          <button 
            className="ml-1 p-1 text-red-500 opacity-0 group-hover:opacity-100 rounded-full"
            title="Remove from project"
            onClick={(e) => {
              e.stopPropagation();
              handleRemoveSession(project.id, session.id, e);
            }}
          >
            <Trash2 size={10} />
          </button>
        )}
      </div>
    ))}
  </div>
) : (
  <div className="py-1 text-xs text-gray-500">
    This project is empty
  </div>
)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-4 py-3 text-xs text-gray-500">
                {searchTerm ? "No matching projects found." : "No projects yet. Create one!"}
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Tooltip for Projects */}
      {hoveredProject && (
        <div 
          className="fixed bg-white border border-gray-200 px-2 py-1 text-xs shadow-md z-50"
          style={{ 
            top: tooltipPosition.top + 'px', 
            left: tooltipPosition.left + 'px',
            fontSize: '12px',
            borderRadius: '2px'
          }}
        >
          {projects.find(p => p.id === hoveredProject)?.project_name}
        </div>
      )}

      {/* Tooltip for Sessions within Projects */}
        {hoveredSession && (
        <div 
          className="fixed bg-white border border-gray-200 px-2 py-1 text-xs shadow-md z-50"
          style={{ 
            top: tooltipPosition.top + 'px', 
            left: tooltipPosition.left + 'px',
            fontSize: '12px',
            borderRadius: '2px'
            }}
           >
          {(() => {
        //← First try local sessions context (own sessions)
            const localSession = sessions.find(s => s.id === hoveredSession);
            if (localSession) return localSession.title;

        // ← Then search through all projectSessions (shared sessions)
            for (const projectId in projectSessions) {
            const found = projectSessions[projectId].find(
            s => s.id === hoveredSession
           );
             if (found) return found.title;
            }

           return "";
          })()}
        </div>
      )}
      {shareDialogProject && (
        <ShareProjectDialog
  isOpen={shareDialogOpen}
  onClose={() => {
    setShareDialogOpen(false);
    setShareDialogProject(null);
  }}
  projectId={shareDialogProject.id}
  projectName={shareDialogProject.name}
  currentUserId={currentUserId}
  isOwner={shareDialogProject.isOwner}
/>
      )}
      {deleteDialogProject && (
        <DeleteProjectDialog
          isOpen={deleteDialogProject !== null}
          onClose={() => setDeleteDialogProject(null)}
          projectName={deleteDialogProject.project_name}
          conversationCount={(deleteDialogProject.session_ids || []).length}
          isDeleting={deletingProjectId === deleteDialogProject.id}
          retentionHours={userDetails?.deletion_retention_hours}
          onConfirm={(deleteBrainBanks) =>
            handleProjectDelete(deleteDialogProject.id, null, {
              cascade: true,
              deleteBrainBanks,
            })
          }
        />
      )}
    </div>
    
  );
};
export default Projects;